<script lang="ts">
  /**
   * Days-in-Now meter: 7 pill ticks, `min(days, 7)` filled in the stay tone
   * (fresh / lingering / stale, see `stayTone`). Past a week every tick is filled;
   * the label carries the real count.
   *
   * Screen readers get one announcement: the visible `label` when one is passed
   * (the ticks are hidden), otherwise an `img` named "N days in Now".
   */
  import { stayTone } from '../lib/nowStay';

  const TICKS = 7;

  interface Props {
    days: number;
    variant?: 'light' | 'onDark';
    size?: 'sm' | 'lg';
    /** Visible label next to the ticks, e.g. `stayLabel(item)`. Colored by tone. */
    label?: string;
  }

  let { days, variant = 'light', size = 'sm', label }: Props = $props();

  let tone = $derived(stayTone(days));
  let filled = $derived(Math.max(0, Math.min(Math.floor(days), TICKS)));
  let spokenLabel = $derived(`${days} ${days === 1 ? 'day' : 'days'} in Now`);
</script>

<span
  class="stay-meter {tone} {variant} {size}"
  data-tone={tone}
  role={label ? undefined : 'img'}
  aria-label={label ? undefined : spokenLabel}
>
  <span class="ticks" aria-hidden="true">
    {#each { length: TICKS } as _, i (i)}
      <span class="tick" class:filled={i < filled}></span>
    {/each}
  </span>
  {#if label}
    <span class="label">{label}</span>
  {/if}
</span>

<style>
  .stay-meter {
    --tick-empty: #e6e1d9;
    --tone: #a8a29a;
    /* Label text: the tone, darkened where the tick color is too light to read on
       a light surface (WCAG AA for small text). */
    --tone-text: #6b655d;
    --tick-w: 0.75rem;
    --tick-h: 0.3125rem;
    --tick-gap: 0.1875rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-variant-numeric: tabular-nums;
  }

  .stay-meter.lg {
    --tick-w: 1.125rem;
    --tick-h: 0.4375rem;
    --tick-gap: 0.25rem;
    gap: 0.625rem;
  }

  .stay-meter.lingering {
    --tone: #d97706;
    --tone-text: #b45309;
  }

  .stay-meter.stale {
    --tone: #dc2626;
    --tone-text: #dc2626;
  }

  .stay-meter.onDark {
    --tick-empty: rgba(255, 255, 255, 0.14);
    --tone-text: #c9c2b9;
  }

  .stay-meter.onDark.lingering {
    --tone: #fbbf24;
    --tone-text: #fbbf24;
  }

  .stay-meter.onDark.stale {
    --tone: #f87171;
    --tone-text: #f87171;
  }

  .ticks {
    display: inline-flex;
    gap: var(--tick-gap);
    flex-shrink: 0;
  }

  .tick {
    width: var(--tick-w);
    height: var(--tick-h);
    border-radius: 999px;
    background: var(--tick-empty);
  }

  .tick.filled {
    background: var(--tone);
  }

  .label {
    color: var(--tone-text);
    font-weight: 600;
    white-space: nowrap;
  }
</style>
